import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import type { ExternalSearchResponse, ExternalSearchResult, SourceSearchResult } from '../services/types';

export function SearchNovelsPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<ExternalSearchResponse | null>(null);
  const [crawlingUrls, setCrawlingUrls] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setError('請輸入搜尋關鍵字');
      return;
    }

    if (trimmedQuery.length < 2) {
      setError('搜尋關鍵字至少需要 2 個字');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      const result = await api.searchExternal(trimmedQuery, 20);
      setSearchResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '搜尋失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCrawl = async (sourceUrl: string) => {
    setCrawlingUrls((prev) => new Set(prev).add(sourceUrl));

    try {
      const novel = await api.createNovel(sourceUrl);
      navigate(`/novels/${novel.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '啟動搜刮失敗');
      setCrawlingUrls((prev) => {
        const next = new Set(prev);
        next.delete(sourceUrl);
        return next;
      });
    }
  };

  const getTotalResults = () => {
    if (!searchResult) return 0;
    return Object.values(searchResult.results).reduce(
      (sum, source) => sum + source.items.length,
      0
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="heading-page">搜尋小說</h1>
        <p className="body-base mt-1">
          跨來源搜尋小說，找到後可直接啟動搜刮
        </p>
      </div>

      {/* 搜尋表單 */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="輸入小說名稱或作者..."
            className="input flex-1"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary btn-md whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                搜尋中...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                搜尋
              </span>
            )}
          </button>
        </div>
      </form>

      {/* 錯誤訊息 */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm mb-6">
          {error}
        </div>
      )}

      {/* 搜尋結果 */}
      {searchResult && (
        <div className="space-y-6">
          {/* 結果統計 */}
          <div className="text-sm text-neutral-500 dark:text-neutral-400">
            搜尋「{searchResult.query}」找到 {getTotalResults()} 筆結果
          </div>

          {/* 按來源分類顯示 */}
          {Object.entries(searchResult.results).map(([adapterId, sourceResult]) => (
            <SourceResultCard
              key={adapterId}
              adapterId={adapterId}
              result={sourceResult}
              crawlingUrls={crawlingUrls}
              onStartCrawl={handleStartCrawl}
            />
          ))}

          {/* 無結果 */}
          {getTotalResults() === 0 && (
            <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">沒有找到相關小說</p>
              <p className="text-sm mt-1">試試其他關鍵字</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 來源結果卡片
function SourceResultCard({
  adapterId,
  result,
  crawlingUrls,
  onStartCrawl,
}: {
  adapterId: string;
  result: SourceSearchResult;
  crawlingUrls: Set<string>;
  onStartCrawl: (url: string) => void;
}) {
  return (
    <div className="card">
      {/* 來源標題 */}
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-900 dark:text-neutral-50">
            {result.source.name}
          </span>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            ({result.source.baseUrl})
          </span>
          {result.items.length > 0 && (
            <span className="badge badge-primary">{result.items.length} 筆</span>
          )}
        </div>
        <a
          href={result.searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost btn-sm"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          預覽原網站
        </a>
      </div>

      {/* 來源內容 */}
      <div className="card-body">
        {/* 錯誤狀態 */}
        {!result.success && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            搜尋失敗：{result.error || '未知錯誤'}
          </div>
        )}

        {/* 無結果 */}
        {result.success && result.items.length === 0 && (
          <div className="text-center py-6 text-neutral-500 dark:text-neutral-400 text-sm">
            此來源沒有找到相關小說
          </div>
        )}

        {/* 結果列表 */}
        {result.success && result.items.length > 0 && (
          <div className="space-y-3">
            {result.items.map((item, index) => (
              <SearchResultItem
                key={`${adapterId}-${index}`}
                item={item}
                isCrawling={crawlingUrls.has(item.sourceUrl)}
                onStartCrawl={onStartCrawl}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 搜尋結果項目
function SearchResultItem({
  item,
  isCrawling,
  onStartCrawl,
}: {
  item: ExternalSearchResult;
  isCrawling: boolean;
  onStartCrawl: (url: string) => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex items-start gap-4 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
      {/* 封面 */}
      {item.coverUrl ? (
        <img
          src={item.coverUrl}
          alt=""
          className="w-16 h-20 object-cover rounded-md flex-shrink-0"
        />
      ) : (
        <div className="w-16 h-20 bg-neutral-200 dark:bg-neutral-700 rounded-md flex-shrink-0 flex items-center justify-center">
          <svg className="w-8 h-8 text-neutral-400 dark:text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
      )}

      {/* 資訊 */}
      <div className="flex-1 min-w-0">
        <a
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-neutral-900 dark:text-neutral-50 hover:text-primary-500 dark:hover:text-primary-400 truncate block transition-colors"
        >
          {item.title}
        </a>
        {item.author && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            作者：{item.author}
          </p>
        )}
        {item.latestChapter && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 truncate">
            最新：{item.latestChapter}
          </p>
        )}
        {item.status && (
          <span className={`inline-block mt-1 badge ${item.status === 'completed' ? 'badge-success' : 'badge-neutral'}`}>
            {item.status === 'completed' ? '已完結' : '連載中'}
          </span>
        )}
      </div>

      {/* 操作按鈕 */}
      <div className="flex-shrink-0">
        {item.existingNovelId ? (
          <button
            onClick={() => navigate(`/novels/${item.existingNovelId}`)}
            className="btn btn-secondary btn-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            已收藏
          </button>
        ) : isCrawling ? (
          <button disabled className="btn btn-primary btn-sm opacity-50">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
            啟動中...
          </button>
        ) : (
          <button
            onClick={() => onStartCrawl(item.sourceUrl)}
            className="btn btn-primary btn-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            啟動搜刮
          </button>
        )}
      </div>
    </div>
  );
}
